const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.myhomehero.co').replace(
  /\/$/,
  ''
);

const DEVICE_ID_KEY = 'homehero_admin_device_id';
const SESSION_KEY = 'homehero_admin_session';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const getDeviceUniqueId = (): string => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-admin-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

export const getStoredToken = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || null;
  } catch {
    return null;
  }
};

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  /** Use an explicit token instead of the stored session token */
  token?: string;
};

export type PaginationMeta = {
  itemsPerPage: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
};

type ApiPayload<T> = {
  success?: boolean;
  message?: string;
  data?: T;
  pagination?: PaginationMeta;
};

async function parseResponse<T>(
  response: Response
): Promise<ApiPayload<T>> {
  let payload: ApiPayload<T> | null = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || payload?.success === false) {
    throw new ApiError(payload?.message || 'Something went wrong', response.status);
  }

  return payload || {};
}

function buildHeaders(options: RequestOptions): Record<string, string> {
  const { auth = false, token: explicitToken } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    devicemodel: 'web-admin',
    deviceuniqueid: getDeviceUniqueId(),
  };

  const token = explicitToken || (auth ? getStoredToken() : null);
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: buildHeaders(options),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const payload = await parseResponse<T>(response);
  return (payload.data ?? payload) as T;
}

export async function apiRequestPaginated<T>(
  path: string,
  options: RequestOptions = {}
): Promise<{ data: T; pagination: PaginationMeta }> {
  const { method = 'GET', body } = options;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: buildHeaders(options),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const payload = await parseResponse<T>(response);
  return {
    data: (payload.data ?? {}) as T,
    pagination: payload.pagination || {
      itemsPerPage: 10,
      currentPage: 1,
      totalItems: 0,
      totalPages: 0,
    },
  };
}
